import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DRIVER_IMAGE, DRIVER_CONTACT_WHATSAPP } from "../../lib/troy";
import { Users, Wallet, TrendingUp, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: Users, title: "Muitos passageiros", desc: "Acesso diário a corridas na sua região." },
  { icon: Wallet, title: "Ganhos semanais", desc: "Pagamento por corrida, você controla seu tempo." },
  { icon: TrendingUp, title: "Mais corridas", desc: "Demanda crescente em General Carneiro e bairros próximos." },
];

const INITIAL = {
  name: "",
  whatsapp: "",
  city: "Sabará",
  neighborhood: "General Carneiro",
  vehicle_type: "",
  referral: "",
  car_model: "",
  car_plate: "",
  car_year: "",
  car_color: "",
  cnh_category: "",
  cnh_ear: "",
  experience_years: "",
  availability: "",
  notes: "",
};

const CNH_OPTIONS = ["A", "B", "AB", "C", "D", "E"];
const EAR_OPTIONS = [
  { value: "sim", label: "Sim" },
  { value: "nao", label: "Não" },
];
const EXPERIENCE_OPTIONS = [
  "Menos de 1 ano",
  "1 a 3 anos",
  "3 a 5 anos",
  "Mais de 5 anos",
];
const VEHICLE_OPTIONS = ["Carro", "Moto"];
const AVAILABILITY_OPTIONS = [
  "Manhã",
  "Tarde",
  "Noite",
  "Madrugada",
  "Fim de semana",
  "Tempo integral",
];

// Traduz erro Pydantic para mensagem amigável em PT-BR
const FIELD_LABELS = {
  name: "Nome",
  whatsapp: "WhatsApp",
  city: "Cidade",
  neighborhood: "Bairro",
  vehicle_type: "Tipo de veículo",
  referral: "Indicação",
  car_model: "Modelo do carro",
  car_plate: "Placa",
  car_year: "Ano do carro",
  car_color: "Cor",
  cnh_category: "Categoria CNH",
  experience_years: "Experiência",
  availability: "Disponibilidade",
  notes: "Observações",
};

function parseApiError(err) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    const field = first?.loc?.[first.loc.length - 1];
    const label = FIELD_LABELS[field] || field || "Campo";
    if (first?.type?.includes("string_too_short")) {
      return `${label}: precisa de mais caracteres.`;
    }
    return `${label}: ${first?.msg || "valor inválido"}`;
  }
  if (typeof detail === "string") return detail;
  if (err?.message) return `Erro de rede: ${err.message}`;
  return "Erro ao enviar cadastro. Tente novamente.";
}

export default function Drivers() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [waLink, setWaLink] = useState(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSelectChange = (name) => (value) => setForm((f) => ({ ...f, [name]: value }));

  const buildWhatsAppMessage = (data) => {
    const lines = ["*Novo cadastro de motorista Troy*", ""];
    const rows = [
      ["Nome", data.name],
      ["WhatsApp", data.whatsapp],
      ["Cidade", data.city],
      ["Bairro", data.neighborhood],
      ["Tipo de veículo", data.vehicle_type],
      ["Modelo do carro", data.car_model],
      ["Placa", data.car_plate],
      ["Ano", data.car_year],
      ["Cor", data.car_color],
      ["Categoria CNH", data.cnh_category],
      ["CNH com EAR", data.cnh_ear ? (data.cnh_ear === "sim" ? "Sim" : "Não") : ""],
      ["Experiência", data.experience_years],
      ["Disponibilidade", data.availability],
      ["Quem indicou", data.referral],
      ["Observações", data.notes],
    ];
    rows.forEach(([k, v]) => {
      if (v && String(v).trim()) lines.push(`*${k}:* ${String(v).trim()}`);
    });
    lines.push("", "Quero fazer parte do grupo Troy Transportes!");
    return lines.join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.city || !form.car_model) {
      toast.error("Preencha os campos obrigatórios: nome, WhatsApp, cidade e modelo do carro.");
      return;
    }
    if (form.name.trim().length < 2) {
      toast.error("Nome precisa de pelo menos 2 caracteres.");
      return;
    }
    const digits = form.whatsapp.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("WhatsApp inválido. Inclua DDD + número (ex: 31 99999-9999).");
      return;
    }

    setLoading(true);
    try {
      // Normaliza payload: converte "" opcional para undefined
      const payload = { ...form, cnh_ear: form.cnh_ear ? form.cnh_ear === "sim" : undefined };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });

      await axios.post(`${API}/drivers`, payload);

      const msg = buildWhatsAppMessage(form);
      const waUrl = `https://wa.me/55${DRIVER_CONTACT_WHATSAPP}?text=${encodeURIComponent(msg)}`;

      // Card de sucesso fica como reserva caso o redirecionamento não abra o WhatsApp
      setWaLink(waUrl);
      setForm(INITIAL);
      toast.success("Cadastro salvo! Abrindo o WhatsApp...", { duration: 8000 });

      // Envio automático: redireciona na mesma aba (funciona em celular, sem bloqueio de popup)
      window.location.href = waUrl;
    } catch (err) {
      toast.error(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="motoristas"
      data-testid="drivers-section"
      className="relative py-24 md:py-32 bg-[#0B0B0E] border-y border-white/5"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_80%_30%,rgba(255,59,48,0.12),transparent_50%)]" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.25em] uppercase text-[#FF3B30] font-bold mb-3">
            Para motoristas
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-6 text-balance">
            Vire motorista parceiro da{" "}
            <span className="text-[#FFCC00]">Troy</span>.
          </h2>
          <p className="text-white/70 text-lg leading-relaxed max-w-xl">
            Cadastre-se como motorista parceiro e receba passageiros direto pelo
            grupo da Troy. Sem algoritmo, sem bloqueio injusto — você atende,
            você recebe.
          </p>

          <div className="mt-8 rounded-3xl overflow-hidden border border-[#FFCC00]/15 aspect-[16/10] relative">
            <img
              src={DRIVER_IMAGE}
              alt="Motorista parceiro sorrindo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-sm text-white/60">Motorista parceiro</p>
              <p className="font-display text-2xl font-bold">Samuel — General Carneiro</p>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="p-5 rounded-2xl bg-[#18181C] border border-[#FFCC00]/15"
                data-testid={`driver-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <b.icon className="w-5 h-5 text-[#FFCC00] mb-3" />
                <h3 className="font-bold mb-1">{b.title}</h3>
                <p className="text-sm text-white/60">{b.desc}</p>
              </div>
            ))}
          </div>

          <p
            data-testid="drivers-cta-helper"
            className="mt-8 text-white/60 text-sm leading-relaxed max-w-md"
          >
            Preencha o formulário ao lado. Seus dados são enviados direto para o
            WhatsApp da Troy — em minutos nossa equipe entra em contato para te
            ativar como motorista parceiro.
          </p>
        </div>

        <div className="lg:col-span-7">
          {waLink ? (
            <div
              data-testid="driver-success-card"
              className="relative rounded-3xl bg-[#121215] border border-[#FFCC00]/15 p-8 md:p-12 lg:sticky lg:top-28 text-center"
            >
              <div className="absolute -inset-1 bg-[#25D366]/10 blur-3xl rounded-3xl -z-10" />
              <CheckCircle2 className="w-16 h-16 text-[#25D366] mx-auto mb-5" />
              <h3 className="font-display text-2xl md:text-3xl font-black mb-3">
                Cadastro salvo com sucesso!
              </h3>
              <p className="text-white/60 mb-8 max-w-sm mx-auto">
                O WhatsApp deve abrir automaticamente. Se não abrir, toque no
                botão abaixo para enviar seus dados para a Troy.
              </p>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="driver-open-whatsapp-btn"
                className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-[#25D366] text-black font-bold py-4 px-10 rounded-xl hover:bg-[#1FB955] transition-colors text-lg shadow-[0_0_24px_rgba(37,211,102,0.35)]"
              >
                <MessageCircle className="w-6 h-6" />
                Enviar pelo WhatsApp
              </a>
              <button
                type="button"
                data-testid="driver-new-registration-btn"
                onClick={() => setWaLink(null)}
                className="block mx-auto mt-6 text-sm text-white/50 hover:text-white underline underline-offset-4"
              >
                Fazer um novo cadastro
              </button>
            </div>
          ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="driver-registration-form"
            className="card-accent-top relative rounded-3xl bg-[#121215] border border-[#FFCC00]/15 p-7 md:p-10 lg:sticky lg:top-28"
          >
            <div className="absolute -inset-1 bg-[#FFCC00]/10 blur-3xl rounded-3xl -z-10" />
            <p className="text-xs tracking-[0.25em] uppercase text-[#FFCC00] font-bold mb-2">
              Cadastre-se
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-black mb-2">
              Seja um motorista Troy
            </h3>
            <p className="text-white/50 text-sm mb-6">
              Quanto mais informações, mais rápido a gente te ativa.
            </p>

            <div className="space-y-5">
              {/* --- Dados pessoais --- */}
              <SectionLabel>Seus dados</SectionLabel>
              <Field label="Nome completo *" htmlFor="name">
                <TextInput id="name" name="name" value={form.name} onChange={onChange} placeholder="Ex: João da Silva" testid="driver-field-name" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="WhatsApp *" htmlFor="whatsapp">
                  <TextInput id="whatsapp" name="whatsapp" value={form.whatsapp} onChange={onChange} placeholder="(31) 9 9999-9999" testid="driver-field-whatsapp" />
                </Field>
                <Field label="Cidade *" htmlFor="city">
                  <TextInput id="city" name="city" value={form.city} onChange={onChange} placeholder="Sabará" testid="driver-field-city" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Bairro" htmlFor="neighborhood">
                  <TextInput id="neighborhood" name="neighborhood" value={form.neighborhood} onChange={onChange} placeholder="General Carneiro" testid="driver-field-neighborhood" />
                </Field>
                <Field label="Quem indicou? (opcional)" htmlFor="referral">
                  <TextInput id="referral" name="referral" value={form.referral} onChange={onChange} placeholder="Nome de quem indicou" testid="driver-field-referral" />
                </Field>
              </div>

              {/* --- Veículo --- */}
              <SectionLabel>Sobre o veículo</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Tipo de veículo" htmlFor="vehicle_type">
                  <StyledSelect
                    value={form.vehicle_type}
                    onValueChange={onSelectChange("vehicle_type")}
                    placeholder="Carro ou moto?"
                    testid="driver-field-vehicle-type"
                    options={VEHICLE_OPTIONS.map((v) => ({ value: v, label: v }))}
                  />
                </Field>
                <Field label="Modelo do carro *" htmlFor="car_model">
                  <TextInput id="car_model" name="car_model" value={form.car_model} onChange={onChange} placeholder="Ex: HB20" testid="driver-field-car" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Ano" htmlFor="car_year">
                  <TextInput id="car_year" name="car_year" value={form.car_year} onChange={onChange} placeholder="Ex: 2020" testid="driver-field-year" />
                </Field>
                <Field label="Cor" htmlFor="car_color">
                  <TextInput id="car_color" name="car_color" value={form.car_color} onChange={onChange} placeholder="Ex: Prata" testid="driver-field-color" />
                </Field>
              </div>
              <Field label="Placa" htmlFor="car_plate">
                <TextInput id="car_plate" name="car_plate" value={form.car_plate} onChange={onChange} placeholder="ABC-1D23" testid="driver-field-plate" />
              </Field>

              {/* --- CNH / Experiência --- */}
              <SectionLabel>Habilitação e experiência</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Categoria CNH" htmlFor="cnh_category">
                  <StyledSelect
                    value={form.cnh_category}
                    onValueChange={onSelectChange("cnh_category")}
                    placeholder="Selecione"
                    testid="driver-field-cnh"
                    options={CNH_OPTIONS.map((v) => ({ value: v, label: v }))}
                  />
                </Field>
                <Field label="CNH com EAR?" htmlFor="cnh_ear">
                  <StyledSelect
                    value={form.cnh_ear}
                    onValueChange={onSelectChange("cnh_ear")}
                    placeholder="Selecione"
                    testid="driver-field-ear"
                    options={EAR_OPTIONS}
                  />
                </Field>
                <Field label="Experiência" htmlFor="experience_years">
                  <StyledSelect
                    value={form.experience_years}
                    onValueChange={onSelectChange("experience_years")}
                    placeholder="Selecione"
                    testid="driver-field-experience"
                    options={EXPERIENCE_OPTIONS.map((v) => ({ value: v, label: v }))}
                  />
                </Field>
              </div>
              <Field label="Disponibilidade" htmlFor="availability">
                <StyledSelect
                  value={form.availability}
                  onValueChange={onSelectChange("availability")}
                  placeholder="Quando você pode rodar?"
                  testid="driver-field-availability"
                  options={AVAILABILITY_OPTIONS.map((v) => ({ value: v, label: v }))}
                />
              </Field>

              {/* --- Notas --- */}
              <Field label="Observações (opcional)" htmlFor="notes">
                <Textarea
                  id="notes"
                  name="notes"
                  data-testid="driver-field-notes"
                  value={form.notes}
                  onChange={onChange}
                  placeholder="Conte um pouco sobre você, indicações, etc."
                  rows={3}
                  className="bg-[#1C1C22] border-white/10 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="driver-submit-btn"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-bold py-4 px-8 rounded-xl hover:bg-[#E6B800] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_24px_rgba(255,204,0,0.3)]"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Enviando..." : "Enviar cadastro pelo WhatsApp"}
            </button>
            <p className="mt-3 text-xs text-white/50 text-center">
              Ao enviar, seus dados vão direto para o WhatsApp da Troy — respondemos rapidinho pra te ativar.
            </p>
          </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-white/80 text-sm font-semibold mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span className="h-px flex-1 bg-white/10" />
      <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold">
        {children}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}

function TextInput({ id, name, value, onChange, placeholder, testid }) {
  return (
    <Input
      id={id}
      name={name}
      data-testid={testid}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="bg-[#1C1C22] border-white/10 h-12 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
    />
  );
}

function StyledSelect({ value, onValueChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        data-testid={testid}
        className="bg-[#1C1C22] border-white/10 h-12 rounded-xl text-white data-[placeholder]:text-white/30 focus:ring-[#FFCC00] focus:border-[#FFCC00]"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#18181C] border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="focus:bg-[#FFCC00]/10 focus:text-[#FFCC00]">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
